import { FC, memo, useCallback, useMemo } from "react";
import type { CompanyFilters, Lookups } from "../types";
import type { FilterModule } from "./index";
import { useCompanyFilterSlice, useCompanyFilterActions } from "../hooks";
import { normalizeNumberArray, normalizeMinMax } from "../utils/normalize";
import { renderFilterDetailList } from "../components/FilterDetailList";
import { mapIdsToNames } from "../utils/mapIdsToNames";
import CollectionSelect from "@/components/CollectionSelect/CollectionSelect";
import { ProductAttributesInput } from "@/components/filters/ProductAttributes/ProductAttributesInput";
import { InstallAgeSection } from "@/components/filters/UserFilters/InstallAgeFilter/InstallAgeSection";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";

export type ProductAttributesSlice = {
  ids: number[];
  installAgeMonths?: { min?: number; max?: number };
};

export const defaultProductAttributesSlice: ProductAttributesSlice = {
  ids: [],
  installAgeMonths: undefined,
};

const ProductAttributesFilter: FC = memo(() => {
  const slice = useCompanyFilterSlice("productAttributes");
  const { setSlice } = useCompanyFilterActions();

  const selectedItems = useMemo(() => slice.ids.map(String), [slice.ids]);

  const handleProductAttributesSelect = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) {
        setSlice("productAttributes", defaultProductAttributesSlice);
        return;
      }
      setSlice("productAttributes", {
        ...slice,
        ids: ids.map(Number),
      });
    },
    [setSlice, slice]
  );

  const handleInstallAgeChange = useCallback(
    (installAge: { min?: number | null; max?: number | null }) => {
      setSlice("productAttributes", {
        ...slice,
        installAgeMonths:
          installAge.min === undefined && installAge.max === undefined
            ? undefined
            : {
                min: installAge.min ?? undefined,
                max: installAge.max ?? undefined,
              },
      });
    },
    [setSlice, slice]
  );

  return (
    <div id="product-attributes-filter-container">
      <Box marginBottom={1}>
        <Typography variant="body2">
          This filter helps you find companies using products with specific
          attributes, ranging from AI capabilities to electronic services.
        </Typography>
      </Box>

      <Stack direction="column" gap={1}>
        <ProductAttributesInput
          selectedItems={selectedItems}
          onProductAttributesSelect={handleProductAttributesSelect}
          id="segments-product-attributes-input"
        />

        <CollectionSelect
          placeholder="Search Collections"
          type="product_attribute"
          onSelect={({ ids }) =>
            handleProductAttributesSelect([
              ...selectedItems,
              ...(ids as string[]).filter((id) => !selectedItems.includes(id)),
            ])
          }
        />
      </Stack>

      <InstallAgeSection
        disabled={selectedItems.length === 0}
        value={slice.installAgeMonths || { min: undefined, max: undefined }}
        onChange={handleInstallAgeChange}
        filterHasSelection={selectedItems.length > 0}
        sx={{ mb: 1 }}
      />
    </div>
  );
});

ProductAttributesFilter.displayName = "ProductAttributesFilter";

export const productAttributesModule: FilterModule<
  ProductAttributesSlice,
  Lookups
> = {
  key: "productAttributes",
  defaultSlice: defaultProductAttributesSlice,
  Component: ProductAttributesFilter,
  getChips: (slice, setSlice, lookups) => {
    if (slice.ids.length === 0) return [];

    const attributes = lookups?.attributes ?? [];

    // Get names for tooltip
    const names = mapIdsToNames(
      slice.ids,
      attributes,
      (id) => `Attribute ${id}`
    );

    // Generate smart label: show name if single selection, count otherwise
    const label =
      slice.ids.length === 1 && names[0]
        ? names[0]
        : `${slice.ids.length} selected`;

    // Build tooltip content
    const tooltipContent =
      names.length > 0
        ? renderFilterDetailList("Attributes", names)
        : undefined;

    return [
      {
        id: "productAttributes",
        label: `Product Attributes: ${label}`,
        filterKey: "productAttributes" as const,
        onRemove: () => setSlice({ ids: [], installAgeMonths: undefined }),
        tooltipContent,
      },
    ];
  },
  getCount: (slice) => {
    return slice.ids.length + (slice.installAgeMonths ? 1 : 0);
  },
  toCompanyFilters: (slice) => {
    if (slice.ids.length === 0) return {};
    const out: NonNullable<CompanyFilters["productAttributes"]> = {
      ids: slice.ids,
    };
    if (slice.installAgeMonths) {
      out.installAgeMonths = slice.installAgeMonths;
    }
    return { productAttributes: out };
  },
  fromCompanyFilters: (filters) => {
    const pa = filters.productAttributes;
    if (!pa) return defaultProductAttributesSlice;
    return {
      ids: normalizeNumberArray(pa.ids),
      installAgeMonths: normalizeMinMax(pa.installAgeMonths),
    };
  },
};
